import type { MarkdownCalloutType } from "@refinement-canvas/domain";
import { contrastRatio } from "./color-contrast";
import {
  MARKDOWN_TABLE_BODY_BACKGROUND_COLOR,
  MARKDOWN_TABLE_ROW_STRIPE_COLOR,
  MARKDOWN_TABLE_ROW_STRIPE_COLOR_ON_DARK,
} from "./markdown-text";
import { isValidHexColor, normalizeHexColor } from "./hex-color";
import { resolveTextBackground, TEXT_DEFAULT_COLOR } from "./text-color";

/** コンテナで上書きするリンク色トークン。既知のキーだけを許可する。 */
export type TextLinkColorVariable =
  | "--color-link"
  | "--color-link-hover"
  | "--color-link-visited"
  | "--color-link-visited-hover";

/** 上書きに使う暗背景用トークンの名前。上と 1 対 1 で対応する。 */
export type TextLinkColorOnDarkVariable =
  | "--color-link-on-dark"
  | "--color-link-on-dark-hover"
  | "--color-link-on-dark-visited"
  | "--color-link-on-dark-visited-hover";

/**
 * styles.css の明背景用・暗背景用トークンと一致する 4 状態の色。テストで整合を確認する。
 *
 * 値はデザインシステム（明電デザインシステム）に合わせる。
 * - 明背景: 通常 = Blue-500、hover = Blue-300、visited = デザインシステムの Visited 色。
 *   visited hover は規定が無いため「White を 20% 混色」（Blue-500 → Blue-400 と同じ規則）で導出する。
 * - 暗背景: デザインシステム「ダークモード」の読み替え値（Blue-500 / Blue-300 / visited）。
 *   visited hover は同じ規則で読み替え値の visited から導出する。
 * styles.css 側は var() 参照で書かれているため、ここには解決後の hex を持つ。
 */
export const TEXT_LINK_COLOR_PAIRS: readonly {
  readonly variable: TextLinkColorVariable;
  readonly onDarkVariable: TextLinkColorOnDarkVariable;
  readonly light: string;
  readonly dark: string;
}[] = [
  {
    variable: "--color-link",
    onDarkVariable: "--color-link-on-dark",
    light: "#2142c0",
    dark: "#8fa0df",
  },
  {
    variable: "--color-link-hover",
    onDarkVariable: "--color-link-on-dark-hover",
    light: "#6a80d5",
    dark: "#b9c4ec",
  },
  {
    variable: "--color-link-visited",
    onDarkVariable: "--color-link-on-dark-visited",
    light: "#6b20c0",
    dark: "#c4a6e5",
  },
  {
    variable: "--color-link-visited-hover",
    onDarkVariable: "--color-link-on-dark-visited-hover",
    light: "#894dcd",
    dark: "#d0b8ea",
  },
];

/** React に依存せず、コンテナのリンク色だけを表す style 上書き。 */
export type TextLinkColorVariables = {
  readonly [K in TextLinkColorVariable]?: string | undefined;
};

/** 暗い背景で切り替える表の行ストライプ用 CSS カスタムプロパティ。 */
export type TableRowStripeColorVariables = {
  readonly "--ds-table-body-bg"?: string | undefined;
  readonly "--ds-table-row-stripe"?: string | undefined;
};

/** 表本文の奇数・偶数行へ使う解決済みの不透明な配色。 */
export interface MarkdownTableBodyPalette {
  readonly bodyBackground: string;
  readonly stripeBackground: string;
  readonly onDark: boolean;
}

/** 表本文のセルを描画するときだけ渡す、行面と本文色の解決済み context。 */
export interface MarkdownTableBodyCellContext {
  readonly bodyRowIndex: number;
  readonly background: string;
  readonly bodyColor: string;
  readonly palette: MarkdownTableBodyPalette;
}

/** 表本文のリンク run が状態別の最終色を受け取る CSS カスタムプロパティ。 */
export type MarkdownTableLinkColorVariables = {
  readonly "--canvas-md-table-link-color": string;
  readonly "--canvas-md-table-link-hover-color": string;
  readonly "--canvas-md-table-link-visited-color": string;
  readonly "--canvas-md-table-link-visited-hover-color": string;
};

/** 暗い背景で切り替える callout の accent / 背景用 CSS カスタムプロパティ。 */
export type CalloutColorVariables = {
  readonly [K in `--callout-${MarkdownCalloutType}-${"accent" | "bg"}`]?: string | undefined;
};

/** テキスト本文コンテナへまとめて与える色トークンの上書き。 */
export type TextContentColorVariables = TextLinkColorVariables &
  TableRowStripeColorVariables &
  CalloutColorVariables;

/**
 * 暗い背景のテキスト図形のコンテナへ与える CSS カスタムプロパティ。
 * .canvas-linkified-link は var(--color-link) 系を参照するため、コンテナで上書きすると
 * 子孫のリンクへ継承される（付箋の sticky-object.tsx と同じ仕組み）。
 * 値は hex ではなくトークン参照にし、色の定義を styles.css の 1 か所に保つ。
 * TEXT_LINK_COLOR_PAIRS との対応（キーと参照先トークン）は単体テストで固定する。
 */
export const TEXT_LINK_ON_DARK_STYLE_VARIABLES: TextLinkColorVariables = {
  "--color-link": "var(--color-link-on-dark)",
  "--color-link-hover": "var(--color-link-on-dark-hover)",
  "--color-link-visited": "var(--color-link-on-dark-visited)",
  "--color-link-visited-hover": "var(--color-link-on-dark-visited-hover)",
};

/** 暗い背景の本文コンテナで使う表の行ストライプ色。 */
export const TABLE_ON_DARK_STYLE_VARIABLES: TableRowStripeColorVariables = {
  "--ds-table-row-stripe": "var(--ds-table-row-stripe-on-dark)",
};

/** 解決済みの表本文配色を、表 DOM が参照する CSS 変数へ変換する。 */
export function tableBodyPaletteStyleVariables(
  palette: MarkdownTableBodyPalette
): TableRowStripeColorVariables {
  return {
    "--ds-table-body-bg": palette.bodyBackground,
    ...(palette.onDark ? TABLE_ON_DARK_STYLE_VARIABLES : {}),
  };
}

/**
 * 暗い背景の図形のコンテナへ与える callout の上書き。リンク色の上書きと同時に適用する。
 * 薄い背景が地色へ埋もれ左罫も沈むため、Connect Note のダーク値へ切り替える。
 * 値は hex ではなくトークン参照にし、色の定義を styles.css の 1 か所に保つ。
 */
export const CALLOUT_ON_DARK_STYLE_VARIABLES: CalloutColorVariables = {
  "--callout-info-accent": "var(--callout-info-accent-on-dark)",
  "--callout-info-bg": "var(--callout-info-bg-on-dark)",
  "--callout-warn-accent": "var(--callout-warn-accent-on-dark)",
  "--callout-warn-bg": "var(--callout-warn-bg-on-dark)",
  "--callout-alert-accent": "var(--callout-alert-accent-on-dark)",
  "--callout-alert-bg": "var(--callout-alert-bg-on-dark)",
  "--callout-note-accent": "var(--callout-note-accent-on-dark)",
  "--callout-note-bg": "var(--callout-note-bg-on-dark)",
};

/**
 * テキスト図形の背景色（fill）に対して、暗い背景用のリンク色を使うべきかを判定する。
 *
 * 判定規則:
 * - 背景色が hex でない（未設定・"transparent"・非 hex）なら従来色（false）
 * - 4 状態すべてで「暗い背景用色のコントラスト比 > ライト側色のコントラスト比」が
 *   成り立つときだけ true。1 つでも等しいか下回るなら false
 *
 * 状態ごとに明背景用色と暗背景用色の輝度が異なるため、切り替えの損益分岐点（背景の相対輝度）も
 * 状態ごとに異なる（通常 0.182 〜 hover 0.365）。1 つの状態だけで判定すると、中間の明度の背景
 * （例 #959595）で hover は改善する一方で通常色のコントラストが悪化する。4 状態を同じ配色体系へ
 * そろえたまま、どの状態も悪化させないため全状態の改善を条件にする。
 */
export function prefersLinkColorOnDark(fill: string | undefined): boolean {
  const background = resolveTextBackground(fill);
  if (background === undefined) return false;
  return TEXT_LINK_COLOR_PAIRS.every(
    (pair) => contrastRatio(background, pair.dark) > contrastRatio(background, pair.light)
  );
}

/**
 * 図形の実際の塗りと本文色から、表本文の奇数・偶数行に使う不透明な配色を返す。
 * 不正色や透明指定は必ず通常の白 / 薄いグレーへ倒し、透明な行へ戻さない。
 */
export function resolveTableBodyPalette({
  fill,
  bodyColor,
}: {
  readonly fill: string | undefined;
  readonly bodyColor: string;
}): MarkdownTableBodyPalette {
  const background = resolveTextBackground(fill);
  if (background === undefined) {
    return {
      bodyBackground: MARKDOWN_TABLE_BODY_BACKGROUND_COLOR,
      stripeBackground: MARKDOWN_TABLE_ROW_STRIPE_COLOR,
      onDark: false,
    };
  }
  const normalizedBodyColor = isValidHexColor(bodyColor)
    ? normalizeHexColor(bodyColor)
    : TEXT_DEFAULT_COLOR;
  const onDark =
    prefersLinkColorOnDark(fill) ||
    contrastRatio(normalizedBodyColor, MARKDOWN_TABLE_ROW_STRIPE_COLOR_ON_DARK) >
      contrastRatio(normalizedBodyColor, MARKDOWN_TABLE_ROW_STRIPE_COLOR);
  return onDark
    ? {
        bodyBackground: background,
        stripeBackground: MARKDOWN_TABLE_ROW_STRIPE_COLOR_ON_DARK,
        onDark: true,
      }
    : {
        bodyBackground: MARKDOWN_TABLE_BODY_BACKGROUND_COLOR,
        stripeBackground: MARKDOWN_TABLE_ROW_STRIPE_COLOR,
        onDark: false,
      };
}

/**
 * 表本文の描画色を、実際に下へ敷く面とのコントラスト 4.5:1 で検査する。
 * 適合済みの指定色は維持し、不足時だけ黒または白のうち読みやすい側へ補正する。
 */
export function resolveMarkdownTableForeground(
  candidate: string,
  effectiveBackground: string
): string {
  const foreground = isValidHexColor(candidate) ? normalizeHexColor(candidate) : TEXT_DEFAULT_COLOR;
  const background = isValidHexColor(effectiveBackground)
    ? normalizeHexColor(effectiveBackground)
    : MARKDOWN_TABLE_BODY_BACKGROUND_COLOR;
  if (contrastRatio(foreground, background) >= 4.5) return foreground;
  return contrastRatio("#000000", background) >= contrastRatio("#ffffff", background)
    ? "#000000"
    : "#ffffff";
}

/** 表本文リンクの各状態を run の実効背景に対して個別に補正する。 */
export function resolveMarkdownTableLinkColorVars({
  explicitColor,
  effectiveBackground,
  onDark,
}: {
  readonly explicitColor?: string | undefined;
  readonly effectiveBackground: string;
  readonly onDark: boolean;
}): MarkdownTableLinkColorVariables {
  const candidates = TEXT_LINK_COLOR_PAIRS.map((pair) =>
    isValidHexColor(explicitColor)
      ? normalizeHexColor(explicitColor)
      : onDark
        ? pair.dark
        : pair.light
  );
  return {
    "--canvas-md-table-link-color": resolveMarkdownTableForeground(
      candidates[0]!,
      effectiveBackground
    ),
    "--canvas-md-table-link-hover-color": resolveMarkdownTableForeground(
      candidates[1]!,
      effectiveBackground
    ),
    "--canvas-md-table-link-visited-color": resolveMarkdownTableForeground(
      candidates[2]!,
      effectiveBackground
    ),
    "--canvas-md-table-link-visited-hover-color": resolveMarkdownTableForeground(
      candidates[3]!,
      effectiveBackground
    ),
  };
}

/** 表の行ストライプだけを背景の明暗に合わせる。明背景では継承値を変えない。 */
export function resolveTableRowStripeColorVars(
  fill: string | undefined
): TableRowStripeColorVariables {
  return prefersLinkColorOnDark(fill) ? TABLE_ON_DARK_STYLE_VARIABLES : {};
}

/**
 * callout の accent / 背景だけを背景の明暗に合わせる。明背景では継承値を変えない。
 * リンク色を自前で解決する付箋（sticky-object.tsx）が、行ストライプと並べて使う。
 */
export function resolveCalloutColorVars(fill: string | undefined): CalloutColorVariables {
  return prefersLinkColorOnDark(fill) ? CALLOUT_ON_DARK_STYLE_VARIABLES : {};
}

/** コンテナ style へ spread する上書き。従来色のままなら空オブジェクト（DOM を変えない）。 */
export function resolveTextLinkColorVars(fill: string | undefined): TextContentColorVariables {
  return prefersLinkColorOnDark(fill)
    ? {
        ...TEXT_LINK_ON_DARK_STYLE_VARIABLES,
        ...TABLE_ON_DARK_STYLE_VARIABLES,
        ...CALLOUT_ON_DARK_STYLE_VARIABLES,
      }
    : {};
}
